import { useState } from 'react';
import { ApiError } from '@/api/client';
import { useFormFactor } from '@/lib/formFactor';
import { Icon } from '@/components/Icon';
import { Tag } from '@/components/Tag';
import { updateAccount } from './api';
import { effectivePerms, scopeBrief, summarizeRole } from './permissions';
import { activeState } from './status';
import type { Role, UserRow } from './types';
import s from './AccountFormSheet.module.css';

const READONLY = import.meta.env.VITE_READONLY !== '0';
const PASSWORD_CHARS = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%';

function temporaryPassword() {
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  return [...bytes].map((value) => PASSWORD_CHARS[value % PASSWORD_CHARS.length]).join('');
}

export function AccountSettingsSheet({ account, roles, canEdit, canStatus, canReset, onClose, onSaved }: {
  account: UserRow;
  roles: Role[];
  canEdit: boolean;
  canStatus: boolean;
  canReset: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const phone = useFormFactor() === 'phone';
  const [name, setName] = useState(account.name);
  const [roleCode, setRoleCode] = useState(account.roleCode || account.role);
  const [active, setActive] = useState(account.isActive);
  const [saving, setSaving] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const [error, setError] = useState('');
  const [resetOpen, setResetOpen] = useState(false);
  const [oneTimePassword, setOneTimePassword] = useState('');
  const [copyError, setCopyError] = useState('');
  const activeRoles = roles.filter((role) => role.isActive || role.code === (account.roleCode || account.role));
  const role = activeRoles.find((item) => item.code === roleCode);
  const previous = roles.find((item) => item.code === (account.roleCode || account.role));
  const changeName = name.trim() !== account.name;
  const changeRole = roleCode !== (account.roleCode || account.role);
  const changeStatus = active !== account.isActive;
  const changed = changeName || changeRole || changeStatus;
  const valid = !!name.trim() && !!role && (!changeName && !changeRole || canEdit) && (!changeStatus || canStatus);
  const submit = async () => {
    if (!changed || !valid || READONLY || saving || uncertain) return;
    setSaving(true); setError('');
    try {
      await updateAccount(account.id, { ...(changeName ? { name: name.trim() } : {}), ...(changeRole ? { role: roleCode } : {}), ...(changeStatus ? { isActive: active } : {}) });
      onSaved();
    } catch (cause) {
      if (!(cause instanceof ApiError) || cause.status === 0 || cause.status >= 500) setUncertain(true);
      setError(cause instanceof Error ? cause.message : '保存账号失败');
    } finally { setSaving(false); }
  };
  const reset = async () => {
    if (!canReset || READONLY || saving || uncertain) return;
    const password = temporaryPassword();
    setSaving(true); setError('');
    try {
      await updateAccount(account.id, { password });
      setOneTimePassword(password);
      setResetOpen(false);
      onSaved();
    } catch (cause) {
      if (!(cause instanceof ApiError) || cause.status === 0 || cause.status >= 500) setUncertain(true);
      setError(cause instanceof Error ? cause.message : '重置密码失败');
    } finally { setSaving(false); }
  };
  const diff = previous && role && role.code !== previous.code ? {
    lost: [...effectivePerms(previous)].filter((item) => !effectivePerms(role).has(item)),
    gained: [...effectivePerms(role)].filter((item) => !effectivePerms(previous).has(item)),
  } : null;
  const status = activeState(active);
  const currentRoleSummary = role ? summarizeRole(role) : null;
  const currentScope = role ? scopeBrief(role, effectivePerms(role)).label : '';
  const currentSummary = currentRoleSummary ? {
    ...currentRoleSummary,
    scopeAction: currentScope === '分配给我' ? '查看分配给自己的订单' : currentScope === '本人创建' ? '查看本人创建的订单' : currentScope === '全部订单' ? '查看全部订单' : currentScope === '不看订单' ? '' : `查看${currentScope}的订单`,
  } : null;
  const closePassword = () => { setOneTimePassword(''); onClose(); };
  return <div className={s.backdrop} onMouseDown={(event) => { if (event.target === event.currentTarget && !saving && !oneTimePassword) onClose(); }}>
    <section className={`${s.sheet} ${s.settingsSheet}`} role="dialog" aria-modal="true" aria-label={`账号设置 · ${account.name}`}>
      {oneTimePassword ? <>
        <header className={`${s.head} ${s.settingsHead}`}><h2>临时密码</h2><button type="button" onClick={closePassword} disabled={saving} aria-label="关闭">×</button></header>
        <div className={s.body}><div className={s.secret}><h3>已重置 {account.name} 的密码</h3><p>{account.name} 在其他设备上的登录已失效。请安全地把临时密码交给本人。</p><output>{oneTimePassword}</output><button type="button" onClick={() => void navigator.clipboard.writeText(oneTimePassword).catch(() => setCopyError('复制失败，请手动选中并复制'))}>复制临时密码</button>{copyError && <p className={s.error} role="alert">{copyError}</p>}<p>只显示这一次；离开此页后无法再次查看。</p></div></div>
        <footer className={`${s.foot} ${s.settingsFoot} ${s.settingsResultFoot}`}><button type="button" className={s.primary} onClick={closePassword}>完成</button></footer>
      </> : <>
        <header className={`${s.head} ${s.settingsHead} ${phone ? s.settingsPhoneHead : ''}`}>
          {phone && <button type="button" className={s.settingsBack} onClick={onClose}><Icon name="back" size={20} /><span>账号</span></button>}
          {phone ? <h2>账号设置</h2> : <h2>账号设置 · {account.name}<small>最近登录 —</small></h2>}
          {phone ? <span className={s.settingsHeadSpacer} /> : <button type="button" onClick={onClose} disabled={saving} aria-label="关闭">×</button>}
        </header>
        <div className={`${s.body} ${phone ? s.settingsPhoneBody : ''}`}>
          {phone && <section className={s.settingsIdentity}><div><h3>{account.name}</h3><Tag tone={status.tone} sm>{status.label}</Tag></div><p><span className="tnum">{account.username}</span> · {role?.name || account.roleDefinition?.name || account.role}</p></section>}
          {phone ? <>
            <section className={s.settingsPhoneSection}>
              <h3>基本信息</h3>
              <div className={s.settingsPhoneCard}>
                <label><span>登录账号</span><input value={account.username} readOnly /></label>
                <label><span>姓名</span><input value={name} maxLength={80} disabled={!canEdit} onChange={(event) => setName(event.target.value)} /></label>
                <label><span>角色</span><select value={roleCode} disabled={!canEdit} onChange={(event) => setRoleCode(event.target.value)}>{activeRoles.map((item) => <option key={item.id} value={item.code}>{item.name}</option>)}</select></label>
              </div>
              <p className={s.settingsHelp}>改角色会立即改变这个账号的全部权限。</p>
            </section>
            <section className={s.settingsPhoneSection}>
              <h3>状态</h3>
              <label className={s.settingsStatusCard}><span>{active ? '启用' : '停用'}</span><input type="checkbox" className={s.settingsSwitch} checked={active} disabled={!canStatus} onChange={(event) => setActive(event.target.checked)} /><small>{active ? '停用后立即清除该账号的全部登录会话' : '停用的账号不能登录'}</small></label>
            </section>
            <section className={s.settingsPhoneSection}>
              <h3>密码</h3>
              <button type="button" className={s.settingsPasswordCard} disabled={!canReset || READONLY || saving || uncertain} onClick={() => setResetOpen(true)}><span className={s.settingsPasswordIcon}><Icon name="key" size={18} /></span><span><b>重置密码</b><small>重置后该账号在其他设备上的登录会全部失效</small></span><Icon name="chev" size={16} className={s.settingsPasswordChevron} /></button>
            </section>
            <section className={s.settingsPhoneCapabilities}>
              <div><h3>{account.name}现在能做什么</h3>{role && <small>来自角色「{role.name}」</small>}</div>
              {currentSummary ? <><p className={s.settingsCan}>{[currentSummary.scopeAction, currentSummary.headline.replace(/[。.]$/, '')].filter(Boolean).join('，')}。</p>{currentSummary.hidden.length > 0 && <p className={s.settingsCannot}>看不到{currentSummary.hidden.join('、')}</p>}</> : <p>没有读到这个账号的角色定义。</p>}
            </section>
          </> : <>
            <div className={`${s.grid} ${s.settingsGrid}`}>
              <label><span>登录账号</span><input value={account.username} readOnly /></label>
              <label><span>姓名</span><input value={name} maxLength={80} disabled={!canEdit} onChange={(event) => setName(event.target.value)} /></label>
              <label className={s.wide}><span>角色</span><select value={roleCode} disabled={!canEdit} onChange={(event) => setRoleCode(event.target.value)}>{activeRoles.map((item) => <option key={item.id} value={item.code}>{item.name}</option>)}</select><small>改角色会立即改变这个账号的全部权限。</small></label>
              <label className={`${s.toggle} ${s.settingsToggle}`}><span>状态</span><span><input type="checkbox" className={s.settingsSwitch} checked={active} disabled={!canStatus} onChange={(event) => setActive(event.target.checked)} />{active ? '启用' : '停用'}<small>停用后立即清除该账号的全部登录会话</small></span></label>
            </div>
            {changeRole && role && <div className={s.summary}><h3>改为「{role.name}」后</h3><p>{summarizeRole(role).headline}</p>{diff && !!diff.lost.length && <p className={s.negative}>将失去 {diff.lost.length} 项当前有效权限；改动会立即生效。</p>}{diff && !!diff.gained.length && <p>将新增 {diff.gained.length} 项有效权限。</p>}</div>}
            {changeStatus && !active && <p className={s.note}>停用后该账号不能登录，现有登录会话会被清除。</p>}
            <div className={s.resetRow}><div><b>重置密码</b><small>重置后该账号在其他设备上的登录会全部失效；系统生成临时密码，不用你输入。</small></div><button type="button" disabled={!canReset || READONLY || saving || uncertain} onClick={() => setResetOpen(true)}>重置密码</button></div>
          </>}
          {READONLY && <p className={s.note}>当前为生产只读模式，窗口可查看，不能提交。</p>}
          {error && <p className={s.error} role="alert">{error}{uncertain && '。结果未确认，请核对账号状态，暂勿重复提交。'}</p>}
          {resetOpen && <div className={`${s.resetConfirm} ${phone ? s.resetConfirmPhone : ''}`} role="dialog" aria-modal="true" aria-label="确认重置密码">
            <section className={phone ? s.resetConfirmCard : undefined}>
              <div className={s.resetConfirmBody}>
                <span className={s.resetConfirmIcon}><Icon name="key" size={20} /></span>
                <h3>重置 {account.name} 的密码？</h3>
                <p>{account.name} 在<strong>所有设备上的登录会立即失效</strong>，旧密码也不能再用。</p>
                <p>系统会生成一组临时密码，只显示一次；建议她首次登录后改成自己的。</p>
                <p className={s.resetUnaffected}><b>不受影响：</b>账号、角色和权限不变，历史操作记录全部保留。</p>
              </div>
              <div className={s.resetConfirmActions}><button type="button" onClick={() => setResetOpen(false)} disabled={saving}>暂不重置</button><button type="button" disabled={saving || uncertain || READONLY} onClick={() => void reset()}>{saving ? '重置中…' : '重置密码'}</button></div>
            </section>
          </div>}
        </div>
        <footer className={`${s.foot} ${s.settingsFoot}`}><button type="button" onClick={onClose} disabled={saving}>取消</button><button type="button" className={s.primary} disabled={!changed || !valid || READONLY || saving || uncertain} onClick={() => void submit()}>{saving ? '保存中…' : '保存'}</button></footer>
      </>}
    </section>
  </div>;
}
